import { useEffect, useState } from "react";
import { getObraById } from "@/lib/obraCache";
import { ClienteCache } from "@/services/clienteCache";
import { supabase } from "@/integrations/supabase/client";
import { formatYmd } from "@/utils/dateUtils";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { NovoUsuarioModal } from "@/components/usuario/NovoUsuarioModal";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Info, MessageCircle, Mail, Users, MapPin, Phone, Building, User, Download, FileText, Link, X, Minus, ChevronDown, Camera, Upload, Settings } from "lucide-react";
import { useWhatsApp } from "@/hooks/useWhatsApp";
import { useUserRole } from "@/hooks/useUserRole";
import { WhatsAppModal } from "@/components/shared/WhatsAppModal";

interface WorkHeaderProps {
  workName: string;
  client: string;
  status: 'active' | 'attention' | 'critical';
  progress: number;
  daysRemaining: number;
  obraId?: string;
  onMinimize: () => void;
  isMinimized: boolean;
}

const statusConfig = {
  active: {
    label: "Em dia",
    variant: "default" as const,
    color: "bg-primary"
  },
  attention: {
    label: "Atenção",
    variant: "secondary" as const,
    color: "bg-warning"
  },
  critical: {
    label: "Crítico",
    variant: "destructive" as const,
    color: "bg-destructive"
  }
};

export function WorkHeader({
  workName,
  client,
  status,
  progress,
  daysRemaining,
  obraId,
  onMinimize,
  isMinimized
}: WorkHeaderProps) {
  const statusInfo = statusConfig[status];

  // Workspace single-tenant: o dono da conta vê os documentos da própria obra
  const canViewDocuments = true;

  // Obra real (obraCache) — alimenta endereço, datas e contatos do cliente
  const obra = obraId ? getObraById(obraId) : undefined;
  const NAO_INFORMADO = "Não informado";
  const enderecoReal = obra
    ? [
        [obra.endereco?.rua, obra.endereco?.numero].filter(Boolean).join(", "),
        obra.endereco?.cidade,
        obra.endereco?.estado,
      ].filter(Boolean).join(" - ")
    : "";
  const formatObraDate = (d?: string) => {
    if (!d) return NAO_INFORMADO;
    return d.includes("T") ? new Date(d).toLocaleDateString("pt-BR") : formatYmd(d);
  };

  // Hook para WhatsApp
  const {
    openWhatsApp,
    openWhatsAppWithRDOLink,
    isLoading: isWhatsAppLoading
  } = useWhatsApp();

  // Work details data — dados reais da obra (campos ausentes no cadastro
  // aparecem como "Não informado"; fases não existem no modelo atual)
  const workDetails = {
    name: workName,
    client: client,
    status: statusInfo.label,
    progress: progress,
    address: enderecoReal || NAO_INFORMADO,
    cep: obra?.endereco?.cep || NAO_INFORMADO,
    description: obra?.endereco?.observacao || "",
    startDate: formatObraDate(obra?.dataInicio as string | undefined),
    endDate: formatObraDate(obra?.previsaoTermino as string | undefined),
    budget: NAO_INFORMADO,
    phases: [] as Array<{ name: string; progress: number; status: string }>,
    team: [] as Array<{ name: string; role: string; phone: string; email: string }>,
  };

  // State management
  const [isWorkInfoOpen, setIsWorkInfoOpen] = useState(false);
  const [isWhatsAppModalOpen, setIsWhatsAppModalOpen] = useState(false);
  const [isWhatsAppContactsOpen, setIsWhatsAppContactsOpen] = useState(false);
  const [isEmailContactsOpen, setIsEmailContactsOpen] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    basicInfo: true,
    schedule: true,
    financial: true,
    location: true,
    description: true,
    phases: true,
    team: true
  });
  const [selectedTeamMembers, setSelectedTeamMembers] = useState<string[]>([]);
  const [isTeamMembersOpen, setIsTeamMembersOpen] = useState(false);
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [isNewUserModalOpen, setIsNewUserModalOpen] = useState(false);
  const [userToRemove, setUserToRemove] = useState<{
    index: number;
    name: string;
  } | null>(null);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [workPhoto, setWorkPhoto] = useState<string>("/placeholder.svg");
  const [isEditWorkOpen, setIsEditWorkOpen] = useState(false);
  const [editWorkData, setEditWorkData] = useState(workDetails);

  // Contatos reais do cliente da obra (ClienteCache): telefones + contatos PJ
  const clienteReal = obra?.clienteObra ? ClienteCache.getClienteById(obra.clienteObra) : undefined;
  const clientContacts = [
    ...(clienteReal?.telefones ?? []).map((phone) => ({ name: client, phone })),
    ...(clienteReal?.contatos ?? [])
      .filter((c) => c.telefone)
      .map((c) => ({ name: c.nome, phone: c.telefone as string })),
  ];

  // Usuários reais do sistema (profiles visíveis pela RLS)
  const [workUsers, setWorkUsers] = useState<
    Array<{ name: string; role: string; email: string; phone: string }>
  >([]);
  useEffect(() => {
    (async () => {
      const { data, error } = await supabase.from("profiles").select("full_name, email");
      if (error) { console.error("WorkHeader profiles:", error.message); return; }
      setWorkUsers(
        (data ?? []).map((p) => ({
          name: p.full_name || p.email || "Usuário",
          role: "Responsável",
          email: p.email ?? "",
          phone: "",
        }))
      );
    })();
  }, []);

  // Sem usuários fantasma: novos participantes entram pelo formulário
  const [availableUsers, setAvailableUsers] = useState<
    Array<{ id: string; name: string; role: string; email: string; phone: string }>
  >([]);

  // Handler functions
  const handleOpenWhatsApp = async (phone: string, customMessage?: string) => {
    await openWhatsApp({
      phone,
      text: customMessage || `Olá! Informações sobre a obra ${workName}`
    }, {
      obraId: obraId ?? "",
      date: new Date().toISOString(),
      hasPhone: true,
      source: 'contact_list'
    });
  };

  // Função para adicionar novo usuário à lista e pré-selecioná-lo
  const handleAddNewUser = (userData: {
    name: string;
    email: string;
    role: string;
    phone: string;
  }) => {
    const newUser = {
      id: String(Date.now()),
      // ID único baseado no timestamp
      ...userData
    };
    setAvailableUsers(prev => [...prev, newUser]);
    setSelectedUserId(newUser.id); // Pré-selecionar o novo usuário
    setIsNewUserModalOpen(false);
    setIsAddMemberOpen(true); // Retornar ao modal de adicionar membro
    toast.success(`Usuário ${userData.name} criado e pré-selecionado com sucesso!`);
  };

  // Função para adicionar usuário ao projeto
  const handleAddUserToProject = () => {
    if (!selectedUserId) {
      toast.error("Selecione um usuário para adicionar ao projeto.");
      return;
    }
    const selectedUser = availableUsers.find(u => u.id === selectedUserId);
    if (!selectedUser) {
      toast.error("Usuário não encontrado.");
      return;
    }

    // Adicionar usuário à lista de membros do projeto
    setWorkUsers(prev => [...prev, {
      name: selectedUser.name,
      role: selectedUser.role,
      email: selectedUser.email,
      phone: selectedUser.phone
    }]);

    // Remover usuário da lista de disponíveis
    setAvailableUsers(prev => prev.filter(u => u.id !== selectedUserId));

    // Fechar modal e limpar seleção
    setIsAddMemberOpen(false);
    setSelectedUserId("");
    toast.success(`${selectedUser.name} foi adicionado ao projeto com sucesso!`);
  };

  // Função para remover usuário do projeto
  const handleRemoveUserFromProject = () => {
    if (!userToRemove) return;

    // Usar workUsers (estado) em vez de workDetails.team (mock data)
    const removedUser = workUsers[userToRemove.index];

    // Adicionar o usuário removido de volta à lista de disponíveis
    if (removedUser) {
      setAvailableUsers(prev => [...prev, {
        id: String(Date.now()),
        name: removedUser.name,
        role: removedUser.role,
        email: removedUser.email,
        phone: removedUser.phone
      }]);
    }
    setWorkUsers(prev => prev.filter((_, index) => index !== userToRemove.index));
    setUserToRemove(null);
    toast.success(`${userToRemove.name} foi removido do projeto.`);
  };

  // Função para fazer upload da foto da obra
  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        // 5MB limit
        toast.error("Arquivo muito grande. Máximo 5MB.");
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast.error("Por favor, selecione apenas arquivos de imagem.");
        return;
      }
      const reader = new FileReader();
      reader.onload = e => {
        if (e.target?.result) {
          setWorkPhoto(e.target.result as string);
          toast.success("Foto da obra atualizada com sucesso!");
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleWhatsAppClick = () => {
    if (clientContacts.length === 1) {
      handleOpenWhatsApp(clientContacts[0].phone);
    } else {
      setIsWhatsAppContactsOpen(true);
    }
  };

  const handleEmailSelectionChange = (email: string, checked: boolean) => {
    if (checked) {
      setSelectedEmails([...selectedEmails, email]);
    } else {
      setSelectedEmails(selectedEmails.filter(e => e !== email));
    }
  };

  const handleSendEmails = () => {
    if (selectedEmails.length > 0) {
      const mailto = `mailto:${selectedEmails.join(',')}?subject=Informações da obra ${workName}&body=Olá, seguem informações sobre a obra.`;
      window.location.href = mailto;
      setIsEmailContactsOpen(false);
      setSelectedEmails([]);
    }
  };

  return <>
      <div className="bg-card rounded-lg shadow-card px-6 py-2">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-4 flex-1">
            {/* Foto da Obra - alinhada no topo */}
            <div className="relative w-12 h-12 rounded-full overflow-hidden border-2 border-primary/20 cursor-pointer hover:border-primary/40 transition-all duration-200 flex-shrink-0 mt-1" onClick={() => setIsPhotoModalOpen(true)}>
              <img src={workPhoto} alt="Foto da obra" className="w-full h-full object-cover" onError={e => {
              (e.target as HTMLImageElement).src = "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=400&h=400&fit=crop&crop=center";
            }} />
              <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors duration-200 flex items-center justify-center">
                <Camera className="h-4 w-4 text-white opacity-0 hover:opacity-100 transition-opacity duration-200" />
              </div>
            </div>
            
            {/* Conteúdo do título e informações */}
            <div className="flex-1">
              <div className="flex items-start gap-4 mb-2">
                <h1 className="text-3xl font-bold text-foreground">{workName}</h1>
                <div className="flex items-center gap-3">
                  <button className="inline-flex h-11 w-11 items-center justify-center rounded-lg hover:bg-muted transition-colors sm:h-9 sm:w-9" title="Informação" onClick={() => setIsWorkInfoOpen(true)}>
                    <Info className="w-5 h-5 text-muted-foreground hover:text-foreground" />
                  </button>
                  <button className="inline-flex h-11 w-11 items-center justify-center rounded-lg hover:bg-muted transition-colors sm:h-9 sm:w-9" title="Membros do projeto" onClick={() => setIsTeamMembersOpen(true)}>
                    <Users className="w-5 h-5 text-muted-foreground hover:text-foreground" />
                  </button>
                  <button className="inline-flex h-11 w-11 items-center justify-center rounded-lg hover:bg-muted transition-colors sm:h-9 sm:w-9" title="Whatsapp Cliente" onClick={handleWhatsAppClick}>
                    <MessageCircle className="w-5 h-5 text-muted-foreground hover:text-foreground" />
                  </button>
                  <button className="inline-flex h-11 w-11 items-center justify-center rounded-lg hover:bg-muted transition-colors sm:h-9 sm:w-9" title="E-mails de todos da obra" onClick={() => setIsEmailContactsOpen(true)}>
                    <Mail className="w-5 h-5 text-muted-foreground hover:text-foreground" />
                  </button>
                </div>
              </div>
              {!isMinimized && <>
                  <p className="text-lg text-muted-foreground mb-2">{client}</p>
                  {/* "Projetado/Realizado" removido: não há fonte de progresso
                      físico no modelo atual — sem exibir números inventados. */}
                </>}
            </div>
          </div>

          <div className="flex items-start gap-2">
            {!isMinimized && <div className="text-right space-y-2">
                <Badge variant={statusInfo.variant} className="text-sm px-3 py-1 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => console.log('Clicou no status:', statusInfo.label)}>
                  {statusInfo.label}
                </Badge>
                <div>
                  <div className="text-2xl font-bold text-foreground">{daysRemaining}</div>
                  <div className="text-sm text-muted-foreground">dias restantes</div>
                  <div className={`text-xs mt-1 ${daysRemaining <= 30 ? 'text-destructive' : daysRemaining <= 60 ? 'text-warning' : 'text-muted-foreground'}`}>
                    {daysRemaining <= 30 ? 'Prazo crítico' : daysRemaining <= 60 ? 'Atenção ao prazo' : 'No prazo'}
                  </div>
                </div>
              </div>}
            <Button variant="ghost" size="icon" onClick={onMinimize} className="mt-1 h-11 w-11 shrink-0" title={isMinimized ? "Maximizar" : "Minimizar"} aria-label={isMinimized ? "Maximizar" : "Minimizar"}>
              {isMinimized ? <ChevronDown className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {!isMinimized && <div className="relative h-5 bg-muted rounded-full overflow-hidden mb-2">
            <div className="absolute top-0 left-0 h-full bg-primary/40 rounded-full transition-all duration-300" style={{
          width: '100%'
        }}></div>
            <div className="absolute top-1 left-0 h-3 bg-primary rounded-full transition-all duration-300" style={{
          width: `${progress}%`
        }}></div>
          </div>}
      </div>

      {/* Work Information Dialog */}
      <Dialog open={isWorkInfoOpen} onOpenChange={setIsWorkInfoOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <Building className="h-6 w-6" />
                Informações da Obra
              </DialogTitle>
              {canViewDocuments && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsEditWorkOpen(true)}
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                  title="Editar informações da obra"
                >
                  <Settings className="h-4 w-4" />
                </Button>
              )}
            </div>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh] w-full pr-4">
            <div className="space-y-8">
              {/* Basic Information */}
              <div>
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <Info className="h-5 w-5" />
                  Informações Básicas
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 border rounded-lg">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Nome da Obra</Label>
                    <p className="text-base font-semibold">{workDetails.name}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Cliente</Label>
                    <p className="text-base font-semibold">{workDetails.client}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Status</Label>
                    <Badge variant={statusInfo.variant} className="text-sm">
                      {workDetails.status}
                    </Badge>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Progresso</Label>
                    <div className="flex items-center gap-2">
                      <Progress value={workDetails.progress} className="flex-1" />
                      <span className="text-sm font-medium">{workDetails.progress}%</span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Data de Início</Label>
                    <p className="text-base">{workDetails.startDate}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Previsão de Término</Label>
                    <p className="text-base">{workDetails.endDate}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Orçamento</Label>
                    <p className="text-base font-semibold text-primary">{workDetails.budget}</p>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Location */}
              <div>
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Localização
                </h3>
                <div className="p-4 border rounded-lg">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-base mb-1">{workDetails.address}</p>
                      <p className="text-sm text-muted-foreground">CEP: {workDetails.cep}</p>
                    </div>
                    <Button variant="outline" size="sm" asChild>
                      <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(workDetails.address)}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        Ver no Mapa
                      </a>
                    </Button>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Description */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Descrição</h3>
                <div className="p-4 border rounded-lg">
                  <p className="text-base leading-relaxed">{workDetails.description}</p>
                </div>
              </div>

              <Separator />

              {/* Phases */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Fases do Projeto</h3>
                <div className="space-y-3">
                  {workDetails.phases.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      Nenhuma fase cadastrada para esta obra.
                    </p>
                  )}
                  {workDetails.phases.map((phase, index) => <div key={index} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium">{phase.name}</h4>
                        <div className="flex items-center gap-2">
                          <Badge variant={phase.status === "Concluída" ? "default" : phase.status === "Em andamento" ? "secondary" : "outline"}>
                            {phase.status}
                          </Badge>
                          <span className="text-sm font-semibold">{phase.progress}%</span>
                        </div>
                      </div>
                      <Progress value={phase.progress} />
                    </div>)}
                </div>
              </div>

              <Separator />

              {/* Team */}
              <div>
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Equipe
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {workUsers.map((member, index) => <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <div className="font-medium">{member.name}</div>
                        <div className="text-sm text-muted-foreground">{member.role}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <a href={`whatsapp://send?phone=${member.phone.replace(/\D/g, '')}&text=Olá!`} className="flex items-center gap-1 hover:opacity-80 transition-opacity cursor-pointer" title={`WhatsApp: ${member.phone}`}>
                          <MessageCircle className="w-4 h-4 text-green-600" />
                        </a>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
          </ScrollArea>
          <div className="flex justify-between pt-4">
            <Button variant="outline" onClick={() => setIsExportDialogOpen(true)}>
              <Download className="mr-2 h-4 w-4" />
              Exportar Informações
            </Button>
            <Button onClick={() => setIsWorkInfoOpen(false)}>
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Work Dialog */}
      <Dialog open={isEditWorkOpen} onOpenChange={setIsEditWorkOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-6 w-6" />
              Editar Informações da Obra
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh] w-full pr-4">
            <div className="space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <Info className="h-5 w-5" />
                  Informações Básicas
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border rounded-lg">
                  <div className="space-y-2">
                    <Label htmlFor="edit-name">Nome da Obra</Label>
                    <Input
                      id="edit-name"
                      value={editWorkData.name}
                      onChange={(e) => setEditWorkData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Digite o nome da obra"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-client">Cliente</Label>
                    <Input
                      id="edit-client"
                      value={editWorkData.client}
                      onChange={(e) => setEditWorkData(prev => ({ ...prev, client: e.target.value }))}
                      placeholder="Digite o nome do cliente"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-status">Status</Label>
                    <Select 
                      value={editWorkData.status} 
                      onValueChange={(value) => setEditWorkData(prev => ({ ...prev, status: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Em dia">Em dia</SelectItem>
                        <SelectItem value="Atenção">Atenção</SelectItem>
                        <SelectItem value="Crítico">Crítico</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-progress">Progresso (%)</Label>
                    <Input
                      id="edit-progress"
                      type="number"
                      min="0"
                      max="100"
                      value={editWorkData.progress}
                      onChange={(e) => setEditWorkData(prev => ({ ...prev, progress: parseInt(e.target.value) || 0 }))}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-start-date">Data de Início</Label>
                    <Input
                      id="edit-start-date"
                      value={editWorkData.startDate}
                      onChange={(e) => setEditWorkData(prev => ({ ...prev, startDate: e.target.value }))}
                      placeholder="dd/mm/aaaa"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-end-date">Previsão de Término</Label>
                    <Input
                      id="edit-end-date"
                      value={editWorkData.endDate}
                      onChange={(e) => setEditWorkData(prev => ({ ...prev, endDate: e.target.value }))}
                      placeholder="dd/mm/aaaa"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="edit-budget">Orçamento</Label>
                    <Input
                      id="edit-budget"
                      value={editWorkData.budget}
                      onChange={(e) => setEditWorkData(prev => ({ ...prev, budget: e.target.value }))}
                      placeholder="R$ 0,00"
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Location */}
              <div>
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Localização
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border rounded-lg">
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="edit-address">Endereço</Label>
                    <Input
                      id="edit-address"
                      value={editWorkData.address}
                      onChange={(e) => setEditWorkData(prev => ({ ...prev, address: e.target.value }))}
                      placeholder="Digite o endereço completo"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-cep">CEP</Label>
                    <Input
                      id="edit-cep"
                      value={editWorkData.cep}
                      onChange={(e) => setEditWorkData(prev => ({ ...prev, cep: e.target.value }))}
                      placeholder="00000-000"
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Description */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Descrição</h3>
                <div className="p-4 border rounded-lg">
                  <Textarea
                    value={editWorkData.description}
                    onChange={(e) => setEditWorkData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Digite a descrição do projeto"
                    className="min-h-[100px]"
                  />
                </div>
              </div>
            </div>
          </ScrollArea>
          <div className="flex justify-between pt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setIsEditWorkOpen(false);
                setEditWorkData(workDetails); // Reset to original data
              }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={() => {
                // Save changes logic would go here
                // For now, we'll just show success and close modal
                toast.success("Informações da obra atualizadas com sucesso!");
                setIsEditWorkOpen(false);
                // In a real app, this would update the actual data source
              }}
            >
              Salvar Alterações
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Team Members Dialog */}
      <Dialog open={isTeamMembersOpen} onOpenChange={setIsTeamMembersOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle>Membros do Projeto</DialogTitle>
            <Button onClick={() => setIsAddMemberOpen(true)} className="gap-2">
              <User className="h-4 w-4" />
              Adicionar
            </Button>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh] w-full pr-4">
            <div className="space-y-4">
              {workUsers.map((member, index) => <div key={index} className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors">
                  <div className="flex-1">
                    <div className="font-semibold text-lg">{member.name}</div>
                    <div className="text-sm text-muted-foreground mb-1">Colaborador Interno</div>
                    <div className="text-sm font-medium text-primary">{member.role}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <a href={`mailto:${member.email}`} className="flex items-center gap-2 p-2 rounded-lg border hover:bg-muted transition-colors" title={`Enviar email para ${member.email}`}>
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{member.email}</span>
                    </a>
                    <button onClick={() => handleOpenWhatsApp(member.phone)} className="flex items-center gap-2 p-2 rounded-lg border hover:bg-muted transition-colors" title={`Enviar WhatsApp para ${member.phone}`}>
                      <MessageCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm">{member.phone}</span>
                    </button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => setUserToRemove({
                  index,
                  name: member.name
                })} title={`Remover ${member.name} do projeto`}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>)}
            </div>
          </ScrollArea>
          <div className="flex justify-end pt-4">
            <Button onClick={() => setIsTeamMembersOpen(false)}>
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={isAddMemberOpen} onOpenChange={setIsAddMemberOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Adicionar Membro ao Projeto</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="user-select">Selecionar usuário existente:</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Escolha um usuário..." />
                </SelectTrigger>
                <SelectContent>
                  {availableUsers.map(user => <SelectItem key={user.id} value={user.id}>
                      <div className="flex flex-col">
                        <span className="font-medium">{user.name}</span>
                        <span className="text-sm text-muted-foreground">{user.role}</span>
                      </div>
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-border"></div>
              <span className="text-sm text-muted-foreground">ou</span>
              <div className="flex-1 h-px bg-border"></div>
            </div>
            
            <Button variant="outline" className="w-full gap-2" onClick={() => setIsNewUserModalOpen(true)}>
              <User className="h-4 w-4" />
              Novo Usuário
            </Button>
            
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => {
              setIsAddMemberOpen(false);
              setSelectedUserId("");
            }}>
                Cancelar
              </Button>
              <Button onClick={handleAddUserToProject} disabled={!selectedUserId}>
                Adicionar ao Projeto
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Contacts Selection Dialog */}
      <Dialog open={isWhatsAppContactsOpen} onOpenChange={setIsWhatsAppContactsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Selecionar Contato WhatsApp</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {clientContacts.map((contact, index) => <div key={index} className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent cursor-pointer transition-colors" onClick={() => {
            handleOpenWhatsApp(contact.phone);
            setIsWhatsAppContactsOpen(false);
          }}>
                <div>
                  <div className="font-medium">{contact.name}</div>
                  <div className="text-sm text-muted-foreground">{contact.phone}</div>
                </div>
                <MessageCircle className="h-5 w-5 text-green-600" />
              </div>)}
          </div>
        </DialogContent>
      </Dialog>

      <WhatsAppModal open={isWhatsAppModalOpen} onOpenChange={setIsWhatsAppModalOpen} defaultMessage={`Olá! Seguem informações da obra *${workName}*:\n\n📋 Cliente: ${client}\n📊 Status: ${statusInfo.label}\n📈 Progresso: ${progress}%\n\nLink para mais detalhes: `} obraId="1" source="export_btn" />

      {/* WhatsApp Contacts Selection Dialog */}
      <Dialog open={isEmailContactsOpen} onOpenChange={setIsEmailContactsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Selecionar Destinatários</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {workUsers.map((user, index) => <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center space-x-3">
                  <Checkbox id={`user-${index}`} checked={selectedEmails.includes(user.email)} onCheckedChange={checked => handleEmailSelectionChange(user.email, checked as boolean)} />
                  <div>
                    <div className="font-medium">{user.name}</div>
                    <div className="text-sm text-muted-foreground">{user.email}</div>
                  </div>
                </div>
              </div>)}
          </div>
          <div className="flex justify-between pt-4">
            <Button variant="outline" onClick={e => {
            e.preventDefault();
            setIsEmailContactsOpen(false);
            setSelectedEmails([]);
          }}>
              Cancelar
            </Button>
            <Button onClick={handleSendEmails} disabled={selectedEmails.length === 0}>
              Enviar ({selectedEmails.length})
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* NovoUsuarioModal */}
      <NovoUsuarioModal open={isNewUserModalOpen} onOpenChange={setIsNewUserModalOpen} onUserCreated={handleAddNewUser} />

      {/* Confirmation Dialog for User Removal */}
      <Dialog open={!!userToRemove} onOpenChange={() => setUserToRemove(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-muted-foreground">
              Tem certeza que deseja remover <strong>{userToRemove?.name}</strong> do projeto?
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Esta ação não pode ser desfeita.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setUserToRemove(null)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleRemoveUserFromProject}>
              Remover
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Modal */}
      <Dialog open={isPhotoModalOpen} onOpenChange={setIsPhotoModalOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5" />
              Foto da Obra
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Foto atual expandida */}
            <div className="relative aspect-video w-full rounded-lg overflow-hidden border">
              <img src={workPhoto} alt="Foto da obra expandida" className="w-full h-full object-cover" onError={e => {
              (e.target as HTMLImageElement).src = "/placeholder.svg";
            }} />
            </div>
            
            {/* Botão de upload */}
            <div className="flex items-center justify-center">
              <label htmlFor="photo-upload" className="cursor-pointer">
                <Button variant="outline" className="gap-2" asChild>
                  <span>
                    <Upload className="h-4 w-4" />
                    Alterar Foto
                  </span>
                </Button>
              </label>
              <input id="photo-upload" type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
            </div>
            
            <div className="text-sm text-muted-foreground text-center">
              Formatos aceitos: JPG, PNG, GIF • Tamanho máximo: 5MB
            </div>
          </div>
          
          <div className="flex justify-end pt-4">
            <Button onClick={() => setIsPhotoModalOpen(false)}>
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>;
}